import { acquireApiToken } from "@/lib/auth/msal";
import { apiUrl } from "@/lib/api/client";
import type { EventEnvelope } from "@/lib/api/types";

/**
 * Open an SSE connection for one session and yield typed events.
 *
 * EventSource doesn't accept a custom Authorization header (the
 * standard browser API limits it to the same-origin cookie + a fixed
 * Accept). We work around that by passing the bearer as a query
 * string parameter; the FastAPI side reads it before falling back to
 * the standard Authorization header (Milestone 3.6c will adjust the
 * backend to accept the alternative). For now `EventSource` will
 * 401 if you call it while signed out; the orchestration view gates
 * itself behind RequireAuth, so this only happens in error paths.
 *
 * Returns an `AbortController`-friendly disposer plus an async
 * iterator. The orchestration view consumes events with a `for await`
 * loop until `session.terminal` arrives.
 */

export interface EventStream {
  events: AsyncIterable<EventEnvelope>;
  close: () => void;
}

export async function openEventStream(
  sessionId: string,
): Promise<EventStream> {
  const token = await acquireApiToken();
  // EventSource carries no Authorization header; we'll add ?token=...
  // until the backend accepts the bearer in a query param. For dev
  // against an unauthed local FastAPI, set VITE_PTRN_API_AUTH=off in
  // .env.local to skip the param entirely.
  const skipAuth = import.meta.env.VITE_PTRN_API_AUTH === "off";
  const url = skipAuth
    ? apiUrl(`/sessions/${sessionId}/events`)
    : apiUrl(
        `/sessions/${sessionId}/events?access_token=${encodeURIComponent(
          token,
        )}`,
      );

  const source = new EventSource(url, { withCredentials: false });

  let queueResolve: ((value: IteratorResult<EventEnvelope>) => void) | null = null;
  const buffer: EventEnvelope[] = [];
  let closed = false;

  const handle = (raw: MessageEvent) => {
    // Guard: some SSE frames arrive with no `data:` line (keepalives, named
    // events the server emitted without a body). EventSource gives us
    // raw.data === undefined for those; without this guard JSON.parse turns
    // undefined into the literal string "undefined" and throws. We saw
    // this in prod the moment the user clicked Accept on the recommendation.
    if (raw.data == null || raw.data === "") return;
    try {
      const envelope = JSON.parse(raw.data) as EventEnvelope;
      if (queueResolve) {
        queueResolve({ value: envelope, done: false });
        queueResolve = null;
      } else {
        buffer.push(envelope);
      }
      if (envelope.type === "session.terminal") {
        // Let the consumer's iterator finish, then close the source.
        const settle = () => source.close();
        // queueMicrotask keeps the value emission ordering deterministic.
        queueMicrotask(settle);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Malformed SSE payload", err, raw.data);
    }
  };

  // Attach the same handler to every named event the backend emits;
  // the EventEnvelope.type already discriminates.
  for (const eventName of [
    "session.created",
    "scan.completed",
    "fits.ready",
    "recommendation.ready",
    "parameters.requested",
    "plan.ready",
    "deploy.started",
    "deploy.status",
    "session.terminal",
    "error",
  ]) {
    source.addEventListener(eventName, handle as EventListener);
  }
  // Catch-all (some browsers raise default `message` for unnamed events).
  source.onmessage = handle;

  source.onerror = () => {
    if (closed) return;
    closed = true;
    if (queueResolve) {
      queueResolve({ value: undefined as unknown as EventEnvelope, done: true });
      queueResolve = null;
    }
    source.close();
  };

  const iterator: AsyncIterator<EventEnvelope> = {
    next() {
      if (buffer.length > 0) {
        return Promise.resolve({ value: buffer.shift()!, done: false });
      }
      if (closed) {
        return Promise.resolve({
          value: undefined as unknown as EventEnvelope,
          done: true,
        });
      }
      return new Promise<IteratorResult<EventEnvelope>>((resolve) => {
        queueResolve = resolve;
      });
    },
    return() {
      closed = true;
      source.close();
      return Promise.resolve({
        value: undefined as unknown as EventEnvelope,
        done: true,
      });
    },
  };

  return {
    events: { [Symbol.asyncIterator]: () => iterator },
    close: () => {
      closed = true;
      source.close();
    },
  };
}
